"use client";

import { useState } from "react";
import Image from "next/image";
import { ChevronLeftIcon, ChevronRightIcon, ImageIcon } from "lucide-react";

import { cx } from "@tripcart/traveler-ui/cx";
import { useTranslator } from "@/lib/translation-context";

import styles from "./_card-carousel.client.module.css";

export interface CardImage {
  url: string;
  alt: string;
}

/**
 * Card image area: a swipeable multi-image track with prev/next arrows
 * (revealed on gallery hover/focus — not the whole card) and position dots.
 * Lives above the card's stretched link via z-10 so its controls don't trigger
 * navigation.
 */
export function CardCarousel({
  images,
  title,
  sizes,
  priority = false,
}: {
  images: CardImage[];
  title: string;
  sizes: string;
  priority?: boolean;
}): React.ReactElement {
  const t = useTranslator();
  const [index, setIndex] = useState(0);
  const count = images.length;
  const hasMany = count > 1;

  function go(dir: number, event: React.MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    setIndex((i) => (i + dir + count) % count);
  }

  if (count === 0) {
    return (
      <div className={styles.placeholder} aria-hidden>
        <ImageIcon strokeWidth={1.25} />
      </div>
    );
  }

  return (
    <>
      <div
        className={styles.track}
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {images.map((img, i) => (
          <div key={img.url} className={styles.slide}>
            <Image
              src={img.url}
              alt={i === 0 ? img.alt || title : ""}
              fill
              sizes={sizes}
              priority={priority && i === 0}
              className={styles.image}
            />
          </div>
        ))}
      </div>

      {hasMany ? (
        <>
          <button
            type="button"
            onClick={(e) => go(-1, e)}
            aria-label={t("Previous Image")}
            className={cx(styles.arrow, styles.arrowPrev)}
          >
            <ChevronLeftIcon strokeWidth={2} aria-hidden />
          </button>
          <button
            type="button"
            onClick={(e) => go(1, e)}
            aria-label={t("Next Image")}
            className={cx(styles.arrow, styles.arrowNext)}
          >
            <ChevronRightIcon strokeWidth={2} aria-hidden />
          </button>
          <div className={styles.dots}>
            {images.map((img, i) => (
              <span
                key={img.url}
                className={cx(styles.dot, i === index && styles.dotActive)}
                aria-hidden
              />
            ))}
          </div>
        </>
      ) : null}
    </>
  );
}
